import React, { useEffect, useRef } from "react";

const ITEM_ID_COMMENT_PATTERN = /^\s*item_id:\s*\d+\s*$/;

/**
 * Inserts an HTML comment of the form `<!-- item_id: 123 -->` as the first
 * child of the ref'd element. Updates the comment if `itemId` changes; cleans
 * it up on unmount. Returns the ref to attach to the target element.
 *
 * Used to mark each rendered news/events card with its source item_id so the
 * id is greppable in the live DOM.
 */
export function useItemIdComment<T extends HTMLElement>(itemId?: number | null) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || itemId == null) return;

    let comment: Comment | null = null;
    for (const child of Array.from(el.childNodes)) {
      if (
        child.nodeType === Node.COMMENT_NODE &&
        ITEM_ID_COMMENT_PATTERN.test(child.textContent || "")
      ) {
        comment = child as Comment;
        break;
      }
    }

    if (comment) {
      comment.textContent = ` item_id: ${itemId} `;
    } else {
      comment = document.createComment(` item_id: ${itemId} `);
      el.insertBefore(comment, el.firstChild);
    }

    return () => {
      if (comment && comment.parentNode === el) {
        comment.remove();
      }
    };
  }, [itemId]);

  return ref;
}

interface CardItemIdRootProps extends React.HTMLAttributes<HTMLDivElement> {
  itemId?: number | null;
}

/**
 * Drop-in replacement for the outermost `<div>` of a news/events card. Renders
 * the same div but inserts an HTML comment containing the item_id as the
 * div's first child once mounted.
 */
export function CardItemIdRoot({ itemId, children, ...rest }: CardItemIdRootProps) {
  const ref = useItemIdComment<HTMLDivElement>(itemId);
  return (
    <div ref={ref} {...rest}>
      {children}
    </div>
  );
}

interface RowItemIdRootProps extends React.HTMLAttributes<HTMLTableRowElement> {
  itemId?: number | null;
}

/**
 * Drop-in replacement for `<tr>` when rendering news/events in table format.
 * Inserts an HTML comment containing the item_id as the row's first child.
 * Browsers accept comment nodes inside `<tr>` as children.
 */
export function RowItemIdRoot({ itemId, children, ...rest }: RowItemIdRootProps) {
  const ref = useItemIdComment<HTMLTableRowElement>(itemId);
  return (
    <tr ref={ref} {...rest}>
      {children}
    </tr>
  );
}
